import { useCallback, useMemo } from "react";

/**
 * useStrictInputSanitizer (minimal-risk)
 * - Only flags clearly dangerous patterns.
 * - Character blacklist trimmed; normal punctuation allowed.
 *
 * Returns: (input: string) => {
 *   hasBlacklisted: boolean,
 *   foundChars: string[],
 *   foundPatterns: string[]
 * }
 */
export default function useStrictInputSanitizer() {
    // Keep this tight. We’ll rely mostly on patterns for XSS/SQL/OS stuff.
    const BLACKLIST = useMemo(
        () =>
            Object.freeze([
                "{", "}", "`", "$", "|", "%", "^" // no < or > here anymore
            ]),
        []
    );
    const BLACKLIST_SET = useMemo(() => new Set(BLACKLIST), [BLACKLIST]);

    // Minimal, high-signal patterns only
    const PATTERNS = useMemo(
        () => [
            // XSS
            { id: "SCRIPT_TAG", re: /<script\b[^>]*>[\s\S]*?<\/script>/i },
            { id: "ON_EVENT_HANDLER", re: /\bon\w+\s*=/i },             // onclick=, onload=...
            { id: "JS_URL", re: /\bjavascript:\s*/i },
            { id: "DATA_URL", re: /\bdata:\s*/i },
            // Allow generic <tag> text; DO NOT include ANY_HTML_TAG

            // SQL (only classic dangerous combos)
            //
            // Note: bare "--" and "#" are NOT flagged here (removed
            // SQL_COMMENT_LINE) — real writing and pasted web/Word
            // content uses "--" as an em-dash substitute constantly,
            // and "#" appears in ordinary references like "Skill #3".
            // Django's ORM parameterizes every query, so a lone "--"
            // or "#" in stored text is never interpreted as SQL; the
            // patterns below still catch the actual dangerous
            // combinations (a real SQL comment attack pairs "--" with
            // a quote/keyword, which SQL_TAUTOLOGY and the UNION/DROP/
            // DELETE patterns already cover).
            { id: "SQL_UNION_SELECT", re: /\bUNION\b\s+SELECT\b/i },
            { id: "SQL_DROP_TABLE", re: /\bDROP\b\s+(TABLE|DATABASE)\b/i },
            { id: "SQL_DELETE_FROM", re: /\bDELETE\b\s+FROM\b/i },
            { id: "SQL_COMMENT_BLOCK", re: /\/\*[\s\S]*?\*\// },         // block comments
            { id: "SQL_TAUTOLOGY", re: /\bOR\b\s+\d+\s*=\s*\d+/i },  // OR 1=1

            // NoSQL
            { id: "NOSQL_OPERATOR", re: /\$(?:ne|nin|where|gt|lt|gte|lte|in|regex)\b/i },

            // Template injection
            { id: "TPL_JS_TEMPLATE", re: /\$\{[^}]+\}/ },              // ${...}
            { id: "TPL_MUSTACHE", re: /\{\{[^}]+\}\}/ },            // {{...}}

            // Path traversal
            { id: "PATH_TRAVERSAL", re: /\.\.\/|\.{2}\\+/ },

            // Shell chains (no single pipe), backticks are covered by
            // char blacklist. Bare ";" is intentionally NOT flagged —
            // it's ordinary sentence punctuation ("Identify fractions;
            // explain your reasoning."), and the ORM never executes
            // stored text as a command regardless of its contents.
            { id: "SHELL_CHAIN", re: /(?:\|\||&&)/ },

            // Encoded tricks
            { id: "URL_ENCODED", re: /%[0-9A-Fa-f]{2}/ },
            { id: "HTML_ENTITY", re: /&#x?[0-9A-Fa-f]+;?/ },

            // ⚠️ XML_DTD removed on purpose to avoid false positives like "<!"
            // { id: "XML_DTD",       re: /<!DOCTYPE|<!ENTITY/i },
        ],
        []
    );

    const validate = useCallback((input) => {
        if (typeof input !== "string" || input.length === 0) {
            return { hasBlacklisted: false, foundChars: [], foundPatterns: [] };
        }

        // 1) Char scan
        const foundChars = [];
        for (const ch of input) {
            if (BLACKLIST_SET.has(ch) && !foundChars.includes(ch)) {
                foundChars.push(ch);
            }
        }

        // 2) Pattern scan
        const foundPatterns = [];
        for (const { id, re } of PATTERNS) {
            if (re.global) re.lastIndex = 0;
            if (re.test(input)) foundPatterns.push(id);
        }

        return {
            hasBlacklisted: foundChars.length > 0 || foundPatterns.length > 0,
            foundChars,
            foundPatterns
        };
    }, [BLACKLIST_SET, PATTERNS]);

    return validate;
}
